/**
 * Sync progress maths (the top-bar sync ring, its popover and Settings >
 * Sync status): percent, rate, ETA, stall detection and the short texts the
 * UI shows. Pure functions, no I/O, shared by the API and the web app.
 */

/** No progress for this long while work remains: the feed is stalled. */
export const DEFAULT_STALL_SEC = 15 * 60;

/** Percent done, 0..100 with one decimal; null when the total is unknown or zero. */
export function percentDone(done: number, total: number | null | undefined): number | null {
  if (total === null || total === undefined || !Number.isFinite(total) || total <= 0) return null;
  if (!Number.isFinite(done) || done <= 0) return 0;
  return Math.min(100, Math.round((done / total) * 1000) / 10);
}

/** Items per hour from a count over a window; null when the window is empty. */
export function ratePerHour(count: number, windowSec: number): number | null {
  if (!Number.isFinite(count) || !Number.isFinite(windowSec) || windowSec <= 0) return null;
  if (count <= 0) return 0;
  const r = (count / windowSec) * 3600;
  return r >= 100 ? Math.round(r) : Math.round(r * 10) / 10;
}

/**
 * Seconds until `remaining` is done at `perHour`, never faster than the
 * hourly `cap` (the DEXIS import's 400 films/h limit). Null when nothing
 * remains, or when there is no rate to judge from.
 */
export function etaSeconds(
  remaining: number,
  perHour: number | null | undefined,
  cap?: number | null,
): number | null {
  if (!Number.isFinite(remaining) || remaining <= 0) return null;
  if (perHour === null || perHour === undefined || !Number.isFinite(perHour) || perHour <= 0) {
    return null;
  }
  const rate = cap !== null && cap !== undefined && cap > 0 ? Math.min(perHour, cap) : perHour;
  return Math.round((remaining / rate) * 3600);
}

/**
 * The connector cycle's own rate and ETA: records uploaded over the cycle's
 * elapsed time. Null rate before the first record or the first second.
 */
export function cycleEstimate(
  uploaded: number | null,
  total: number | null,
  elapsedSec: number | null,
): { perHour: number | null; etaSeconds: number | null; percent: number | null } {
  const perHour =
    uploaded !== null && elapsedSec !== null && elapsedSec > 0 && uploaded > 0
      ? ratePerHour(uploaded, elapsedSec)
      : null;
  const remaining = uploaded !== null && total !== null ? Math.max(0, total - uploaded) : 0;
  return {
    perHour,
    etaSeconds: etaSeconds(remaining, perHour),
    percent: uploaded !== null ? percentDone(uploaded, total) : null,
  };
}

/** True when work remains and nothing has moved for `stallSec`. */
export function isStalled(
  lastProgressAt: string | Date | null,
  remaining: number,
  now: number,
  stallSec: number = DEFAULT_STALL_SEC,
): boolean {
  if (remaining <= 0 || lastProgressAt === null) return false;
  const t =
    typeof lastProgressAt === 'string' ? Date.parse(lastProgressAt) : lastProgressAt.getTime();
  if (!Number.isFinite(t)) return false;
  return now - t > stallSec * 1000;
}

/** Combined progress of several bars (sum of done over sum of known totals). */
export function combineProgress(
  items: readonly { done: number; total: number | null }[],
): { done: number; total: number; percent: number | null } | null {
  let done = 0;
  let total = 0;
  for (const i of items) {
    if (i.total === null || i.total <= 0) continue;
    done += Math.min(i.done, i.total);
    total += i.total;
  }
  if (total === 0) return null;
  return { done, total, percent: percentDone(done, total) };
}

/** "under a minute", "12 min", "3 h 20 min", "2 d 4 h". */
export function formatEta(sec: number | null): string | null {
  if (sec === null || !Number.isFinite(sec) || sec < 0) return null;
  if (sec < 60) return 'under a minute';
  const m = Math.round(sec / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 48) return m % 60 === 0 ? `${h} h` : `${h} h ${m % 60} min`;
  const d = Math.floor(h / 24);
  return h % 24 === 0 ? `${d} d` : `${d} d ${h % 24} h`;
}

/** "400/h", "1.2k/h", "0.5/h". */
export function formatRate(perHour: number | null): string | null {
  if (perHour === null || !Number.isFinite(perHour) || perHour < 0) return null;
  if (perHour >= 10_000) return `${Math.round(perHour / 1000)}k/h`;
  if (perHour >= 1000) return `${Math.round(perHour / 100) / 10}k/h`;
  if (perHour >= 10) return `${Math.round(perHour)}/h`;
  return `${Math.round(perHour * 10) / 10}/h`;
}

/** "79,012" in en-US grouping, stable across locales for tests. */
export function formatCount(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}
